import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Paper
} from '@mui/material';
import {
  Security,
  QrCode,
  CheckCircle,
  Warning,
  ContentCopy
} from '@mui/icons-material';

interface MFAStatus {
  enabled: boolean;
  hasBackupCodes: boolean;
  unusedBackupCodes: number;
}

interface MFASetupData {
  secret: string;
  qrCode: string;
  manualEntryKey: string;
  issuer: string;
  accountName: string;
  otpauth_url?: string;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://pawtel-app.onrender.com';

const MFASettings: React.FC = () => {
  const [mfaStatus, setMfaStatus] = useState<MFAStatus | null>(null);
  const [setupDialog, setSetupDialog] = useState(false);
  const [disableDialog, setDisableDialog] = useState(false);
  const [backupCodesDialog, setBackupCodesDialog] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [setupData, setSetupData] = useState<MFASetupData | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [password, setPassword] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchMFAStatus();
  }, []);

  const fetchMFAStatus = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/mfa/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setMfaStatus(data);
      }
    } catch (error) {
      console.error('Error fetching MFA status:', error);
    }
  };

  const setupMFA = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${API_BASE_URL}/api/admin/mfa/setup`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSetupData(data);
        setCurrentStep(1);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to setup MFA');
      }
    } catch (error) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const verifyMFA = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${API_BASE_URL}/api/admin/mfa/verify`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token: verificationCode })
      });

      if (response.ok) {
        const data = await response.json();
        setBackupCodes(data.backupCodes);
        setCurrentStep(2);
        setSuccess('MFA enabled successfully!');
        fetchMFAStatus();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to verify MFA');
      }
    } catch (error) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const disableMFA = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${API_BASE_URL}/api/admin/mfa/disable`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          password,
          token: verificationCode
        })
      });

      if (response.ok) {
        setSuccess('MFA disabled successfully');
        setDisableDialog(false);
        setPassword('');
        setVerificationCode('');
        fetchMFAStatus();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to disable MFA');
      }
    } catch (error) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const regenerateBackupCodes = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${API_BASE_URL}/api/admin/mfa/backup-codes/regenerate`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password })
      });

      if (response.ok) {
        const data = await response.json();
        setBackupCodes(data.backupCodes);
        setSuccess('New backup codes generated');
        setPassword('');
        fetchMFAStatus();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to regenerate backup codes');
      }
    } catch (error) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard');
  };

  const handleSetupDialogClose = () => {
    setSetupDialog(false);
    setCurrentStep(0);
    setSetupData(null);
    setVerificationCode('');
    setBackupCodes([]);
    setError(null);
  };

  const steps = ['Setup MFA', 'Verify Code', 'Save Backup Codes'];

  if (!mfaStatus) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <Security sx={{ mr: 2, color: mfaStatus.enabled ? 'green' : 'orange' }} />
            <Typography variant="h6">
              Multi-Factor Authentication
            </Typography>
            <Chip
              label={mfaStatus.enabled ? 'Enabled' : 'Disabled'}
              color={mfaStatus.enabled ? 'success' : 'warning'}
              sx={{ ml: 2 }}
            />
          </Box>

          <Typography variant="body2" color="textSecondary" mb={3}>
            Add an extra layer of security to your admin account with time-based authentication codes.
          </Typography>

          {!mfaStatus.enabled ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Security />}
              onClick={() => setSetupDialog(true)}
            >
              Enable MFA
            </Button>
          ) : (
            <Box>
              <Typography variant="body2" mb={2}>
                MFA is currently enabled. You have {mfaStatus.unusedBackupCodes} unused backup codes.
              </Typography>
              
              <Box display="flex" gap={2} flexWrap="wrap">
                <Button
                  variant="outlined"
                  onClick={() => setBackupCodesDialog(true)}
                >
                  Generate New Backup Codes
                </Button>
                
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setDisableDialog(true)}
                >
                  Disable MFA
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Setup MFA Dialog */}
      <Dialog open={setupDialog} onClose={handleSetupDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Security sx={{ mr: 1 }} />
            Setup Multi-Factor Authentication
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Stepper activeStep={currentStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {currentStep === 0 && (
            <Box textAlign="center">
              <Typography variant="body1" mb={3}>
                Click "Generate QR Code" to begin setting up MFA for your account.
              </Typography>
              <Button
                variant="contained"
                onClick={setupMFA}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <QrCode />}
              >
                {loading ? 'Generating...' : 'Generate QR Code'}
              </Button>
            </Box>
          )}

          {currentStep === 1 && setupData && (
            <Box>
              <Typography variant="body1" mb={2}>
                1. Install an authenticator app (Google Authenticator, Authy, etc.)
              </Typography>
              <Typography variant="body1" mb={3}>
                2. Scan this QR code or manually enter the key:
              </Typography>
              
              <Box display="flex" justifyContent="center" mb={3}>
                <img 
                  src={setupData.qrCode} 
                  alt="MFA QR Code" 
                  style={{ width: 200, height: 200 }}
                />
              </Box>

              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="body2" color="textSecondary" mb={1}>
                  Manual entry key:
                </Typography>
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', flexGrow: 1 }}>
                    {setupData.manualEntryKey}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => copyToClipboard(setupData.manualEntryKey)}
                    startIcon={<ContentCopy />}
                  >
                    Copy
                  </Button>
                </Box>
              </Paper>

              <Typography variant="body1" mb={2}>
                3. Enter the 6-digit code from your authenticator app:
              </Typography>
              
              <TextField
                fullWidth
                label="Verification Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.2rem' } }}
                sx={{ mb: 2 }}
              />
            </Box>
          )}

          {currentStep === 2 && (
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="h6">MFA Enabled Successfully!</Typography>
              </Alert>
              
              <Typography variant="body1" mb={2}>
                <strong>Important:</strong> Save these backup codes in a secure location. 
                You can use them to access your account if you lose your authenticator device.
              </Typography>
              
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1">Backup Codes</Typography>
                  <Button
                    size="small"
                    onClick={() => copyToClipboard(backupCodes.join('\n'))}
                    startIcon={<ContentCopy />}
                  >
                    Copy All
                  </Button>
                </Box>
                <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={1}>
                  {backupCodes.map((code, index) => (
                    <Typography
                      key={index}
                      variant="body2"
                      sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                    >
                      {code}
                    </Typography>
                  ))}
                </Box>
              </Paper>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleSetupDialogClose}>
            {currentStep === 2 ? 'Done' : 'Cancel'}
          </Button>
          
          {currentStep === 1 && (
            <Button
              variant="contained"
              onClick={verifyMFA}
              disabled={verificationCode.length !== 6 || loading}
            >
              {loading ? <CircularProgress size={20} /> : 'Verify & Enable'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Disable MFA Dialog */}
      <Dialog open={disableDialog} onClose={() => setDisableDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Disable Multi-Factor Authentication</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            This will remove the extra security layer from your account.
          </Alert>
          
          <TextField
            fullWidth
            type="password"
            label="Current Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            label="Current MFA Code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="123456"
            inputProps={{ maxLength: 6 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableDialog(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={disableMFA}
            disabled={!password || verificationCode.length !== 6 || loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Disable MFA'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={backupCodesDialog} onClose={() => setBackupCodesDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate New Backup Codes</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            This will invalidate all existing backup codes.
          </Alert>
          
          <TextField
            fullWidth
            type="password"
            label="Current Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackupCodesDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={regenerateBackupCodes}
            disabled={!password || loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Generate Codes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MFASettings;